import React, { useContext } from "react";
import { ReferralLink } from "types/affiliation.types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import { formatDate } from "@shared/utils/date";
import {
  sortByNumberProperty,
  sortByStringProperty,
} from "@shared/utils/general";
import { checkSearch } from "@shared/utils/misc";
import { AffiliationContext } from "features/affiliation/Affiliation.context";
import useReferralLinks from "features/affiliation/common/hooks/useReferralLinks";
import {
  AffiliationTabs,
  ReferralLinksStatistics,
} from "features/affiliation/common/types";
import {
  copyUrlToClipboard,
  getReferralUrl,
} from "features/affiliation/common/utils";
import ReferralEmptyState from "features/affiliation/common/ReferralEmptyState";
import ReferralLinksTable from "features/affiliation/ReferralLinks/ReferralLinksTable";
import ReferralLinkActions from "features/affiliation/ReferralLinks/ReferralLinkActions";
import TabLayout from "features/affiliation/NewLook/TabLayout";
import TabSummary from "features/affiliation/NewLook/TabSummary";
import TablePanel from "features/affiliation/NewLook/TablePanel";
import RowList, {
  Line,
  RowFigure,
  RowMeta,
  RowSubFigure,
  RowTitle,
} from "features/affiliation/NewLook/RowList";
import { CopyValue } from "features/affiliation/NewLook/parts";
import {
  formatShare,
  formatUsd,
} from "features/affiliation/NewLook/newLookUtils";

/**
 * The referral links tab in the new look: the links' funnel (clicks lead,
 * then registrations, payments and earnings), then the links.
 */
const NewLookReferralLinks = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.AFFILIATION);
  const { hasFeatureFlag } = useContext(AffiliationContext)!;
  const { referralLinksQuery, getStatisticValue } = useReferralLinks();
  const referralLinks = referralLinksQuery.data;
  const loading = referralLinksQuery.isLoading;

  const clicks = getStatisticValue(ReferralLinksStatistics.TOTAL_CLICKS) || 0;
  const referred =
    getStatisticValue(ReferralLinksStatistics.REFERRED_USERS) || 0;
  const paid = getStatisticValue(ReferralLinksStatistics.PAID_USERS) || 0;
  const earned = (referralLinks || []).reduce(
    (sum, link) => sum + link.commission,
    0
  );

  const renderRow = (link: ReferralLink) => {
    const url = getReferralUrl(link.referral_code);
    return (
      <>
        <Line>
          <RowTitle>{link.description || link.referral_code}</RowTitle>
          <ReferralLinkActions referralLinkData={link} largeTrigger={true} />
        </Line>
        <Line>
          <CopyValue
            onClick={() => copyUrlToClipboard(url)}
            isLink={true}
            small={true}
          >
            {url}
          </CopyValue>
          <RowSubFigure>{formatDate(link.time, false, true)}</RowSubFigure>
        </Line>
        <Line>
          <RowMeta>
            {[
              `${t("links.clicks")} ${link.clicks}`,
              `${t("links.registered")} ${link.registered_users}`,
              `${t("links.paid")} ${link.paid_users}`,
            ].join(" · ")}
          </RowMeta>
          <RowFigure>{formatUsd(link.commission)}</RowFigure>
        </Line>
      </>
    );
  };

  return (
    <TabLayout
      data-testid="affiliation-referral-links"
      summary={
        <TabSummary
          loading={loading}
          lead={{
            label: t(ReferralLinksStatistics.TOTAL_CLICKS),
            value: clicks,
          }}
          items={[
            {
              key: "referred",
              label: t(ReferralLinksStatistics.REFERRED_USERS),
              value: referred.toLocaleString("en-US"),
              meta:
                clicks > 0
                  ? t("overview.share_of_clicks", {
                      percent: formatShare(referred / clicks),
                    })
                  : undefined,
            },
            {
              key: "paid",
              label: t(ReferralLinksStatistics.PAID_USERS),
              value: paid.toLocaleString("en-US"),
              meta:
                referred > 0
                  ? t("overview.share_of_referred", {
                      percent: formatShare(paid / referred),
                    })
                  : undefined,
            },
            {
              key: "earned",
              label: t("links.earned"),
              value: formatUsd(earned),
            },
          ]}
        />
      }
      table={
        hasFeatureFlag && (
          <TablePanel
            title={t("links.title")}
            count={referralLinks?.length}
            loading={loading}
            table={
              <ReferralLinksTable
                referralLinks={referralLinks}
                isLoading={referralLinksQuery.isFetching && !loading}
              />
            }
          />
        )
      }
      list={
        hasFeatureFlag && (
          <RowList<ReferralLink>
            title={t("links.title")}
            items={referralLinks}
            getKey={(link) => link.referral_id}
            renderRow={renderRow}
            filter={(link, search) => checkSearch(link.referral_code, search)}
            loading={loading}
            sortOptions={[
              {
                key: "time",
                label: t("links.created_date"),
                compare: (a, b) => sortByNumberProperty(a, b, "time"),
              },
              {
                key: "clicks",
                label: t("links.clicks"),
                compare: (a, b) => sortByNumberProperty(a, b, "clicks"),
              },
              {
                key: "registered_users",
                label: t("links.registered"),
                compare: (a, b) =>
                  sortByNumberProperty(a, b, "registered_users"),
              },
              {
                key: "paid_users",
                label: t("links.paid"),
                compare: (a, b) => sortByNumberProperty(a, b, "paid_users"),
              },
              {
                key: "commission",
                label: t("links.earned"),
                compare: (a, b) => sortByNumberProperty(a, b, "commission"),
              },
              {
                key: "description",
                label: t("links.description"),
                compare: (a, b) => sortByStringProperty(a, b, "description"),
              },
            ]}
            empty={<ReferralEmptyState tab={AffiliationTabs.REFERRAL_LINKS} />}
            searchEmpty={
              <ReferralEmptyState
                tab={AffiliationTabs.REFERRAL_LINKS}
                isSearch={true}
              />
            }
          />
        )
      }
    />
  );
};

export default NewLookReferralLinks;
